/**
 * Analytics — Google Analytics 4 + Hotjar
 *
 * Reads IDs from env vars:
 *   REACT_APP_GA_MEASUREMENT_ID  (G-XXXXXXXXXX)
 *   REACT_APP_HOTJAR_SITE_ID     (numeric site id)
 *   REACT_APP_HOTJAR_VERSION     (default 6)
 *
 * If an ID is the placeholder default (G-XXXXXXXXXX or 0000000) the
 * corresponding script is NOT injected — keeps preview/dev clean.
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const HOTJAR_SITE_ID = process.env.REACT_APP_HOTJAR_SITE_ID || "";
const HOTJAR_VERSION = process.env.REACT_APP_HOTJAR_VERSION || "6";

const isValidGA = (id) => id && id !== "G-XXXXXXXXXX" && /^G-[A-Z0-9]+$/i.test(id);
const isValidHotjar = (id) => id && id !== "0000000" && /^\d+$/.test(id);

let _initialized = false;

export const initAnalytics = () => {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;

  // ----- Google Analytics 4 -----
  if (isValidGA(GA_ID)) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { send_page_view: false });
    // eslint-disable-next-line no-console
    console.info("[analytics] GA4 initialized", GA_ID);
  }

  // ----- Hotjar -----
  if (isValidHotjar(HOTJAR_SITE_ID)) {
    (function (h, o, t, j, a, r) {
      h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
      h._hjSettings = { hjid: parseInt(HOTJAR_SITE_ID, 10), hjsv: parseInt(HOTJAR_VERSION, 10) };
      a = o.getElementsByTagName("head")[0];
      r = o.createElement("script");
      r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
    // eslint-disable-next-line no-console
    console.info("[analytics] Hotjar initialized", HOTJAR_SITE_ID);
  }
};

export const trackPageView = (path, title) => {
  if (!isValidGA(GA_ID) || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

export const trackEvent = (action, params = {}) => {
  if (!isValidGA(GA_ID) || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", action, params);
};

export const identifyUser = (userId, props = {}) => {
  if (typeof window === "undefined") return;
  if (isValidGA(GA_ID) && window.gtag) {
    window.gtag("set", { user_id: userId });
  }
  if (isValidHotjar(HOTJAR_SITE_ID) && window.hj) {
    window.hj("identify", userId, props);
  }
};
